import { AdjustmentMode } from '@local/lib';
import { Card } from '@salesforce/design-system-react';

import type { QualityFormulaPreviewSummary } from '../qualityFormulaTypes';

import { ADJUSTMENT_MODE_COLOR } from '../qualityFormulaMetadata';
import { ResultField } from './ResultField';
import { formatCurrency, formatNumber } from './utils/resultFormatting';

interface SummaryCardProps {
  inline?: boolean;
  summary: QualityFormulaPreviewSummary;
}

// Reuses the one shared Discount/Premium color pair (ADJUSTMENT_MODE_COLOR) rather than a second,
// independently-maintained red/green pair — a value increase reads as a Premium (gain), a
// decrease as a Discount (loss).
const getTotalValueChangeColor = (change: number): string => {
  if (change > 0) return ADJUSTMENT_MODE_COLOR[AdjustmentMode.Premium];
  if (change < 0) return ADJUSTMENT_MODE_COLOR[AdjustmentMode.Discount];
  return 'var(--slds-g-color-on-surface-1)';
};

export const SummaryCard: React.FC<SummaryCardProps> = ({ inline = false, summary }) => {
  const startingTotalValue = summary.startingPrice * summary.startingQuantity;
  const finalTotalValue = summary.finalPrice * summary.finalQuantity;
  const totalValueChange = finalTotalValue - startingTotalValue;

  const finalTotalValueColor = getTotalValueChangeColor(totalValueChange);

  if (inline) {
    return (
      <div className="qfw-result-summary">
        <div className="qfw-result-metric">
          <span>Final Total</span>
          <strong style={{ color: finalTotalValueColor }}>{formatCurrency(finalTotalValue)}</strong>
          <span className="slds-text-color_weak">was {formatCurrency(startingTotalValue)}</span>
        </div>
        <div className="qfw-result-metric">
          <span>Final Price</span>
          <strong>{formatCurrency(summary.finalPrice)}</strong>
          <span className="slds-text-color_weak">was {formatCurrency(summary.startingPrice)}</span>
        </div>
        <div className="qfw-result-metric">
          <span>Final Quantity</span>
          <strong>{formatNumber(summary.finalQuantity)}</strong>
          <span className="slds-text-color_weak">was {formatNumber(summary.startingQuantity)}</span>
        </div>
      </div>
    );
  }

  return (
    <Card className="qfw-result-card qfw-summary-card" heading="Overall Impact Summary">
      <div className="slds-grid slds-wrap slds-gutters_small">
        <div className="slds-col slds-size_1-of-2">
          <ResultField label="Starting Qty" value={formatNumber(summary.startingQuantity)} />
        </div>
        <div className="slds-col slds-size_1-of-2">
          <ResultField label="Final Qty" value={formatNumber(summary.finalQuantity)} />
        </div>
        <div className="slds-col slds-size_1-of-2">
          <ResultField label="Starting Price" value={formatCurrency(summary.startingPrice)} />
        </div>
        <div className="slds-col slds-size_1-of-2">
          <ResultField label="Final Price" value={formatCurrency(summary.finalPrice)} />
        </div>
      </div>
      <hr className="slds-m-vertical_small" />
      <div className="slds-grid slds-wrap slds-gutters_small">
        <div className="slds-col slds-size_1-of-2">
          <ResultField label="Starting Total Value" value={formatCurrency(startingTotalValue)} />
        </div>
        <div className="slds-col slds-size_1-of-2">
          <ResultField color={finalTotalValueColor} label="Final Total Value" value={formatCurrency(finalTotalValue)} />
        </div>
      </div>
    </Card>
  );
};
